import { useEffect, useMemo, useState } from 'react';
import {
  deriveLayerFeedHealth,
  deriveProvincialFireStaleness,
  type FeedHealthResponse,
  type LayerFeedDown,
  type LayerKey,
  type ProvincialFireFeedHealth,
} from '@tsr/shared-types';
import { fetchFeedHealth } from '../api';

/** Re-poll cadence for the global feed-health heartbeat (ms). Independent of the
 *  map bbox — a dead daemon is a dead daemon at any zoom. */
const POLL_MS = 60_000;

export interface FeedHealthView {
  /** Layers whose live feed is currently dark, keyed by layer. Absent key = the
   *  layer's feed is live (or unassessed). */
  byLayer: Map<LayerKey, LayerFeedDown>;
  /** #603 — per-province provincial fire-feed liveness (BC/AB/ON/QC). Distinct
   *  from `byLayer` (which stays "live" while CWFIS feeds): a single dark province
   *  reads "provincial data unavailable", never "0 fires". Empty until first poll. */
  provincialFires: ProvincialFireFeedHealth[];
  /** The DB clock at the last successful poll (epoch ms). The down/live decision
   *  and every displayed age are judged against THIS, not the browser clock, so
   *  the signal is immune to client clock skew. */
  nowMs: number;
}

const EMPTY: FeedHealthView = { byLayer: new Map(), provincialFires: [], nowMs: 0 };

/**
 * Honest feed-health surfacing (#254). Polls `GET /api/feed-health` every ~60s and
 * derives, via the shared worker→layer registry, which COP layers are currently
 * dark ("live tracking down since X"). Returns an empty map until the first poll
 * lands and whenever the endpoint errors — we never fabricate a fault from a
 * failed health check (a missing answer is "unknown", not "down").
 */
export function useFeedHealth(): FeedHealthView {
  const [resp, setResp] = useState<FeedHealthResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();
    const load = async (): Promise<void> => {
      try {
        const body = await fetchFeedHealth(ctl.signal);
        if (!cancelled) setResp(body);
      } catch {
        // Degrade silently: a failed health check must not invent a down badge.
        if (!cancelled && !ctl.signal.aborted) setResp(null);
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(id);
    };
  }, []);

  return useMemo<FeedHealthView>(() => {
    if (!resp) return EMPTY;
    const nowMs = Date.parse(resp.serverNow);
    if (!Number.isFinite(nowMs)) return EMPTY;
    return {
      byLayer: deriveLayerFeedHealth(resp.workers, nowMs),
      provincialFires: deriveProvincialFireStaleness(resp.workers, nowMs),
      nowMs,
    };
  }, [resp]);
}
